import React, { memo, useMemo, useEffect } from 'react';
import { Message, MattressProduct, MessageContent, ChatConfig, ProductRecommendationResponse } from '../types';
import ProductGrid from './ProductGrid';
import ReactMarkdown from 'react-markdown';
import { ImageDisplay, VideoDisplay, FileDisplay, MediaGroup } from './media';
import { 
  hasProductRecommendations, 
  parseProductRecommendations, 
  normalizeRecommendations, 
  getPersonalizedIntro
} from '../utils/productRecommendationUtils';
import { ProductErrorBoundary } from './ErrorBoundary';

// ============================================================
// TYPES
// ============================================================

/**
 * Props for the ChatMessage component
 * @property message - The message object to display
 * @property config - The chat configuration with branding
 * @property onProductSelect - Callback when a product is selected
 */
interface ChatMessageProps {
  message: Message;
  config: ChatConfig | null;
  onProductSelect: (product: MattressProduct) => void;
}

// ============================================================
// UTILITY FUNCTIONS
// ============================================================

/**
 * Converts various timestamp formats to milliseconds
 * @param timestamp - The timestamp to convert
 * @returns The timestamp in milliseconds
 */
const getTimeValue = (timestamp: Date | string | undefined): number => {
  if (timestamp instanceof Date) return timestamp.getTime();
  if (timestamp) return new Date(timestamp).getTime();
  return 0;
};

/**
 * Utility functions for checking content types
 * Used to safely handle different message content types
 */
const contentTypeUtils = {
  /**
   * Checks if content is a simple string
   */
  isString: (content: string | MessageContent): content is string => {
    return typeof content === 'string';
  },
  
  /**
   * Checks if content is an object
   */
  isObject: (content: any): content is Record<string, any> => {
    return typeof content === 'object' && content !== null;
  },
  
  /**
   * Checks if content is an image
   */
  isImageContent: (content: MessageContent): boolean => 
    content.type === 'image' && !!content.image,
  
  /**
   * Checks if content is a video
   */
  isVideoContent: (content: MessageContent): boolean => 
    content.type === 'video' && !!content.video,
  
  /**
   * Checks if content is a file
   */
  isFileContent: (content: MessageContent): boolean => 
    content.type === 'file' && !!content.file,
  
  /**
   * Checks if content is a media group
   */
  isMediaGroupContent: (content: MessageContent): boolean => 
    content.type === 'media_group' && !!content.mediaGroup
};

// Debug flag - set to false to minimize logging
const DEBUG_MODE = false;

/**
 * Logger utility for consistent and controlled logging
 * Only logs in development or when DEBUG_MODE is enabled
 */
const logger = {
  debug: (message: string, ...args: any[]) => {
    if (DEBUG_MODE) {
      console.log(`[ChatMessage Debug] ${message}`, ...args);
    }
  },
  error: (message: string, error: any) => {
    console.error(`[ChatMessage Error] ${message}`, error);
  }
};

// ============================================================
// SUB-COMPONENTS
// ============================================================

/**
 * Debug view component to display raw message data
 * Only visible in development mode
 */
const DebugView = ({ content }: { content: any }) => {
  if (process.env.NODE_ENV !== 'development') return null;
  
  return (
    <details className="mt-2 text-xs border-t pt-2 opacity-50">
      <summary>Debug Info</summary>
      <pre className="overflow-auto max-h-32 p-2 bg-gray-100 rounded text-xs">
        {JSON.stringify(content, null, 2)}
      </pre>
    </details>
  );
};

/**
 * Renders text content, using ReactMarkdown for assistant messages
 * and plain text for user messages
 */
const TextContentRenderer = ({ 
  content, 
  isUser = false, 
  markdownClass = '',
  textColor = '#111827'
}: { 
  content: string, 
  isUser?: boolean,
  markdownClass?: string,
  textColor?: string
}) => {
  // For user messages, just render the text
  if (isUser) {
    return <div className="message-text whitespace-pre-wrap text-base leading-relaxed tracking-wide">
      {content}
    </div>;
  }
  
  // For assistant messages, render as markdown with explicit color applied to override Tailwind prose
  return (
    <div 
      className={`prose prose-slate max-w-none text-base leading-relaxed tracking-wide ${markdownClass}`} 
      style={{ 
        color: textColor,
        // Use CSS variables for prose text colors with string casting to avoid TS errors
        ["--tw-prose-body" as string]: textColor,
        ["--tw-prose-headings" as string]: textColor
      }}
    >
      <ReactMarkdown components={{
        // Override components to ensure text color is applied
        p: ({node, ...props}) => <p style={{color: textColor}} {...props} />,
        li: ({node, ...props}) => <li style={{color: textColor}} {...props} />,
        a: ({node, ...props}) => <a style={{color: textColor}} className="underline" {...props} />
      }}>
        {content}
      </ReactMarkdown>
    </div>
  );
};

/**
 * Renders various media content types (image, video, file, media group)
 * Falls back to showing text content if available
 */
const MediaContentRenderer = ({ content, textColor }: { content: MessageContent, textColor?: string }) => {
  if (contentTypeUtils.isImageContent(content) && content.image) {
    return <ImageDisplay image={content.image} className="mt-2" />;
  } 
  
  if (contentTypeUtils.isVideoContent(content) && content.video) {
    return <VideoDisplay video={content.video} className="mt-2" />;
  } 
  
  if (contentTypeUtils.isFileContent(content) && content.file) {
    return <FileDisplay file={content.file} className="mt-2" />;
  } 
  
  if (contentTypeUtils.isMediaGroupContent(content) && content.mediaGroup) {
    return <MediaGroup mediaGroup={content.mediaGroup} className="mt-2" />;
  }
  
  // Fallback for object content - prefer text field if available
  return (
    <div className="message-text whitespace-pre-wrap" style={{ color: textColor }}>
      {content.text || JSON.stringify(content)}
    </div>
  );
};

/**
 * Renders product recommendations in a grid
 * Uses ProductErrorBoundary to handle rendering errors gracefully
 */
const ProductRecommendationsRenderer = ({ 
  recommendations, 
  config,
  onProductSelect,
  markdownClass,
  textColor
}: { 
  recommendations: ProductRecommendationResponse, 
  config: ChatConfig | null,
  onProductSelect: (product: MattressProduct) => void,
  markdownClass: string,
  textColor?: string
}) => {
  return (
    <ProductErrorBoundary>
      <div className="message-content">
        <ProductGrid 
          recommendations={recommendations}
          onProductSelect={onProductSelect}
          config={config}
        />
      </div>
    </ProductErrorBoundary>
  );
};

/**
 * Renders the avatar for a user or assistant
 * Uses a generic icon for users and the merchant's logo for the assistant
 */
const SenderAvatar = ({ 
  config, 
  isUser, 
  primaryColor, 
  assistantName 
}: { 
  config: ChatConfig | null, 
  isUser: boolean, 
  primaryColor: string, 
  assistantName: string 
}) => {
  if (isUser) {
    return (
      <div className="mr-2 flex-shrink-0 self-start">
        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
          <svg className="w-5 h-5" style={{ color: primaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
          </svg>
        </div>
      </div>
    );
  }
  
  return (
    <div className="ml-2 flex-shrink-0 self-start">
      {config?.branding?.logo ? (
        <div className="w-8 h-8 rounded-full overflow-hidden">
          <img 
            src={config.branding.logo} 
            alt={assistantName} 
            className="w-full h-full object-cover" 
            onError={(e) => {
              // If logo fails to load, show default icon
              const target = e.target as HTMLImageElement;
              target.onerror = null;
              target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI4MCIgaGVpZ2h0PSI4MCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiMyNTYzZWIiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBjbGFzcz0ibHVjaWRlIGx1Y2lkZS1ib3QiPjxwYXRoIGQ9Ik0xMiA4VjRhMiAyIDAgMCAwLTIgMkg3YTIgMiAwIDAgMC0yIDJ2MmEyIDIgMCAwIDAgMiAyaDEwYTIgMiAwIDAgMCAwLTRoLTN6Ii8+PHBhdGggZD0iTTE3IDEydjNhMiAyIDAgMCAxLTIgMkgxMWEyIDIgMCAwIDEtMi0ydi0zIj48L3BhdGg+PHBhdGggZD0iTTEyIDE4djIiPjwvcGF0aD48cmVjdCB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHg9IjIiIHk9IjIiIHJ4PSI1IiByeT0iNSI+PC9yZWN0PjwvZz48L3N2Zz4=';
            }}
          />
        </div>
      ) : (
        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
          <svg className="w-5 h-5" style={{ color: primaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path>
          </svg>
        </div>
      )}
    </div>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================

/**
 * Main ChatMessage component responsible for rendering different types of messages
 * Handles various content types including text, products, and media
 * 
 * @param props - The component props
 * @returns A React component that displays chat messages with appropriate styling
 */
const ChatMessage = memo(({ message, config, onProductSelect }: ChatMessageProps) => {
  // ========== Basic message properties ==========
  // Determine message sender role
  const isUser = message.role === 'user';
  
  // Skip rendering for system messages
  if (message.role !== 'user' && message.role !== 'assistant') return null;
  
  // Extract basic display properties from config
  const assistantName = useMemo(() => {
    return config?.chatConfig?.aiName || config?.storeInfo?.name || config?.branding?.chatTitle || 'MattressAI Assistant';
  }, [config]);
  
  // Extract branding colors from config with fallbacks
  const primaryColor = useMemo(() => 
    config?.branding?.colors?.primary || '#3B82F6', [config]);
  
  const secondaryColor = useMemo(() => 
    config?.branding?.colors?.secondary || '#F3F4F6', [config]);
  
  // Use consistent text colors for both user and assistant messages
  const textColor = useMemo(() => 
    config?.branding?.colors?.text || '#111827', [config]);
  
  // ========== Define reusable styling ==========
  /**
   * CSS classes for markdown content styling
   * Tailwind utility classes for typography
   */
  const markdownClass = useMemo(() => `
    prose prose-sm max-w-none 
    prose-p:my-1 prose-headings:my-3
    prose-ul:my-1 prose-ol:my-1 prose-li:my-0
    prose-pre:my-1 prose-pre:p-2 prose-pre:rounded
    prose-pre:bg-gray-800 prose-code:text-gray-200
    prose-strong:text-inherit prose-a:text-inherit
    hover:prose-a:underline
    prose-p:text-inherit prose-headings:text-inherit prose-li:text-inherit
    prose-h1:text-inherit prose-h2:text-inherit prose-h3:text-inherit
    prose-ul:text-inherit prose-ol:text-inherit
  `, []);
  
  /**
   * Style object for message bubbles
   * Applies consistent styling for both user and assistant messages
   */
  const messageStyle = useMemo(() => {
    // Common styling for both user and assistant from config
    const bubbleStyle = {
      backgroundColor: isUser ? primaryColor : secondaryColor,
      color: textColor // Use the same text color from config for both message types
    };
    
    return bubbleStyle;
  }, [isUser, primaryColor, secondaryColor, textColor]);
  
  // ========== Content processing and rendering logic ==========
  /**
   * Process and render message content based on its type
   * Handles special content like product recommendations
   */
  const renderedContent = useMemo(() => {
    try {
      // Process content to normalize strings, JSON, and objects
      let processedContent = message.content;
      
      logger.debug('Initial content:', JSON.stringify(processedContent, null, 2));
      
      // If content is a string, attempt to parse it for product recommendations
      if (contentTypeUtils.isString(processedContent)) {
        logger.debug('Content is string, attempting to parse');
        processedContent = parseProductRecommendations(processedContent);
        logger.debug('Parsed content:', JSON.stringify(processedContent, null, 2));
      }
      
      // Handle product recommendations (priority rendering)
      if (contentTypeUtils.isObject(processedContent)) {
        logger.debug('Content is object, checking for recommendations');
        
        if (hasProductRecommendations(processedContent)) {
          try {
            // Normalize recommendations to get consistent format
            const recommendations = normalizeRecommendations(processedContent);
            logger.debug('Normalized recommendations:', JSON.stringify(recommendations, null, 2));
            
            // Check if we have valid products
            if (recommendations.products && recommendations.products.length > 0) {
              logger.debug('Rendering ProductRecommendationsRenderer with products');
              
              // Extract just the consultative part of the text, removing product listings
              const consultativeText = processedContent.text ? 
                extractConsultativeText(processedContent.text) : 
                'Based on your needs, here are some options that would work well for you:';
              
              return (
                <div className="product-recommendations-container">
                  <div className={markdownClass} style={{ color: textColor }}>
                    <ReactMarkdown>
                      {consultativeText}
                    </ReactMarkdown>
                  </div>
                  <ProductRecommendationsRenderer
                    recommendations={recommendations}
                    config={config}
                    onProductSelect={onProductSelect}
                    markdownClass={markdownClass}
                    textColor={textColor}
                  />
                </div>
              );
            } else {
              logger.debug('No products found in recommendations');
            }
          } catch (error) {
            logger.error('Error normalizing product recommendations', error);
          }
        }
      }
      
      // Handle text content
      if (contentTypeUtils.isString(processedContent)) {
        return (
          <TextContentRenderer 
            content={processedContent} 
            isUser={isUser}
            markdownClass={markdownClass} 
            textColor={textColor}
          />
        );
      }
      
      // Handle object content (including media)
      if (contentTypeUtils.isObject(processedContent)) {
        const content = processedContent as MessageContent;
        
        // Text content in object format
        if (content.text) {
          return (
            <TextContentRenderer 
              content={content.text} 
              isUser={isUser}
              markdownClass={markdownClass} 
              textColor={textColor}
            />
          );
        }
        
        // Media content
        return <MediaContentRenderer content={content} textColor={textColor} />;
      }
      
      // Fallback for any unhandled content types
      return (
        <div className="message-text whitespace-pre-wrap" style={{ color: textColor }}>
          {typeof processedContent === 'string' 
            ? processedContent 
            : (processedContent && typeof processedContent === 'object' && (processedContent as any).text)
              ? (processedContent as any).text
              : '[Message content is being processed...]'}
        </div>
      );
    } catch (error) {
      logger.error('Error rendering message content', error);
      return (
        <div className="message-error text-red-600 p-2 border border-red-200 rounded bg-red-50">
          [Error rendering message. Please try refreshing the page.]
        </div>
      );
    }
  }, [message.content, message.role, isUser, onProductSelect, config, markdownClass, textColor]);
  
  // ========== Side effects ==========
  /**
   * Ensures all links in product recommendations open in new tabs
   * Attaches click handlers after component renders
   */
  useEffect(() => {
    if (message.role !== 'assistant' || 
        contentTypeUtils.isString(message.content) || 
        !hasProductRecommendations(message.content)) {
      return;
    }
    
    const timer = setTimeout(() => {
      const messageElement = document.querySelector(`[data-message-index="${getTimeValue(message.timestamp)}"]`);
      if (!messageElement) return;
      
      const productLinks = messageElement.querySelectorAll('a[href]');
      
      productLinks.forEach(link => {
        // Clone to remove existing listeners
        const newLink = link.cloneNode(true);
        if (link.parentNode) {
          link.parentNode.replaceChild(newLink, link);
          
          // Add new click handler to open in new tab
          newLink.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const href = (newLink as HTMLAnchorElement).href;
            if (href && href !== '#') {
              window.open(href, '_blank', 'noopener,noreferrer');
            }
            return false;
          });
        }
      });
    }, 100);
    
    return () => clearTimeout(timer);
  }, [message]);
  
  // ========== Final render ==========
  return (
    <div 
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`} 
      data-testid={`message-${isUser ? 'user' : 'assistant'}`}
      data-message-index={getTimeValue(message.timestamp)}
      data-message-role={message.role}
    >
      {/* Render the assistant avatar for non-user messages */}
      {!isUser && (
        <SenderAvatar 
          config={config}
          isUser={isUser}
          primaryColor={primaryColor}
          assistantName={assistantName}
        />
      )}

      {/* Message bubble */}
      <div 
        className={`max-w-[80%] rounded-lg p-4 shadow-sm ${
          isUser ? 'rounded-tr-none' : 'rounded-tl-none'
        }`}
        style={messageStyle}
      >
        {/* Sender name */}
        <div className="font-semibold mb-1" style={{ color: textColor }}>
          {isUser ? '' : assistantName}
        </div>
        
        {/* Message content */}
        <div className="message-content overflow-hidden">
          {renderedContent}
        </div>
        
        {/* Debug view - only shown in development */}
        {process.env.NODE_ENV === 'development' && (
          <DebugView content={message} />
        )}
      </div>
    </div>
  );
});

/**
 * Extracts only the consultative part of a message before product recommendations
 * Eliminates any text that looks like product listings while keeping mattress type recommendations
 */
const extractConsultativeText = (text: string): string => {
  if (!text) return '';
  
  // Identify common product listing markers
  const productListingPhrases = [
    'Here are some', 'Here are our', 'Some options for you', 
    'I recommend the following', 'I\'d recommend these', 
    'Recommended products', 'Top picks', 'Perfect for you'
  ];
  
  // Signal phrases that often come just before product listings
  const listingSignalPhrases = [
    'Here are', 'Some options', 'I recommend', 'Based on your preferences',
    'These mattresses', 'The following options', 'These products'
  ];
  
  // Items that indicate a list is starting
  const listMarkers = [
    /\d+\.\s+/,               // Numbered lists like "1. "
    /[\*\-•]\s+/,             // Bullet points like "• " or "* "
    /\*\*[^*]+\*\*/,          // Bold text like "**Product Name**"
    /^[A-Z][a-z]+ [A-Z][a-z]+:/ // Product names like "Product Name:"
  ];
  
  // Split the text into paragraphs
  const paragraphs = text.split(/\n\n+/);
  const cleanParagraphs = [];
  let stopProcessing = false;
  
  // Process each paragraph
  for (const paragraph of paragraphs) {
    // Skip empty paragraphs
    if (!paragraph.trim()) continue;
    
    // Stop if we've already hit a listing section
    if (stopProcessing) break;
    
    // Check if this paragraph contains listing phrases
    const containsListingPhrase = productListingPhrases.some(phrase => 
      paragraph.toLowerCase().includes(phrase.toLowerCase())
    );
    
    // Check if the paragraph contains a list marker
    const containsListMarker = listMarkers.some(marker => 
      marker.test(paragraph)
    );
    
    // Check if the paragraph is a transition to product listings
    const isTransitionToProdcuts = listingSignalPhrases.some(phrase =>
      paragraph.toLowerCase().includes(phrase.toLowerCase())
    );
    
    // If this paragraph looks like the start of product listings, stop processing
    if (containsListingPhrase || containsListMarker) {
      stopProcessing = true;
      
      // If this is a transition paragraph, try to include only the part before specific products
      if (isTransitionToProdcuts && !containsListMarker) {
        // Find the position of the transition phrase
        const phrasePosition = listingSignalPhrases.reduce((pos, phrase) => {
          const phrasePos = paragraph.toLowerCase().indexOf(phrase.toLowerCase());
          return phrasePos > -1 && (pos === -1 || phrasePos < pos) ? phrasePos : pos;
        }, -1);
        
        // If we found a transition phrase, include everything before it
        if (phrasePosition > 0) {
          cleanParagraphs.push(paragraph.substring(0, phrasePosition).trim());
        }
      }
      
      continue;
    }
    
    // This paragraph seems OK, include it
    cleanParagraphs.push(paragraph);
  }
  
  // If we have a clean introduction, add a smooth transition to the product grid
  if (cleanParagraphs.length > 0) {
    return cleanParagraphs.join('\n\n') + (stopProcessing ? '\n\nHere are some options that match these recommendations:' : '');
  }
  
  // If we couldn't extract a good introduction, return a generic one
  if (stopProcessing) {
    return 'Based on your needs, here are some mattress options that would work well for you:';
  }
  
  // If there were no obvious product listings, return the original text
  return text;
};

export default ChatMessage; 